import React from 'react';
import { Formula } from '../pages/Offerte/types';

interface FormulaCardProps {
  formula: Formula;
  selected?: boolean;
  compact?: boolean;
  // Rendered below the content, e.g. a call-to-action link on the homepage
  footer?: React.ReactNode;
}

const FALLBACK_IMAGE = '/images/ink/formula-classic.jpg';

export const FormulaCard: React.FC<FormulaCardProps> = ({ formula, selected = false, compact = false, footer }) => {
  const perPerson = formula.pricingMode === 'PER_PERSON';

  return (
    <div
      className={`group relative flex h-full flex-col rounded-lg bg-white p-6 text-left transition-all duration-300 ${
        selected
          ? 'ring-2 ring-bl-brown shadow-xl shadow-bl-brown/15'
          : formula.highlighted
            ? 'ring-2 ring-bl-gold shadow-lg'
            : 'ring-1 ring-bl-black/10 shadow-sm'
      } ${footer ? 'hover:-translate-y-1 hover:shadow-xl' : ''}`}
    >
      {formula.highlighted && (
        <span className="eyebrow absolute -top-3.5 left-6 py-1">
          <span aria-hidden="true">★</span> Meest gekozen <span aria-hidden="true">★</span>
        </span>
      )}
      {selected && (
        <span className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-bl-brown text-white" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
          </svg>
        </span>
      )}

      <div className="-mx-6 -mt-6 mb-5 overflow-hidden rounded-t-lg bg-bl-cream">
        <img
          src={`/images/ink/formula-${formula.slug}.jpg`}
          alt=""
          loading="lazy"
          onError={(event) => {
            // Formulas added later in the backoffice have no drawing yet
            const img = event.currentTarget;
            if (!img.src.endsWith(FALLBACK_IMAGE)) img.src = FALLBACK_IMAGE;
          }}
          className={`w-full object-cover mix-blend-multiply transition-transform duration-500 ${compact ? 'aspect-[16/7]' : 'aspect-[4/3]'} ${footer ? 'group-hover:scale-105' : ''}`}
        />
      </div>

      <h3 className="font-display text-2xl font-bold">{formula.name}</h3>
      {formula.tagline && <p className="mt-0.5 text-sm text-bl-brown">{formula.tagline}</p>}

      <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-bl-beige px-3 py-1 text-xs font-medium text-bl-brown-dark">
        {perPerson ? 'Vaste prijs per persoon' : 'Je betaalt enkel wat er gegeten wordt'}
      </p>

      {!compact && formula.description && <p className="mt-4 text-sm text-bl-black/75">{formula.description}</p>}

      {formula.burgers.length > 0 && (
        <ul className={`${compact ? 'mt-3' : 'mt-5'} space-y-1.5 text-sm`}>
          {formula.burgers.map((burger) => (
            <li key={burger} className="flex items-center gap-2.5">
              <span className="h-1.5 w-1.5 flex-none rotate-45 bg-bl-gold" aria-hidden="true" />
              {burger}
            </li>
          ))}
        </ul>
      )}

      {!compact && formula.vegetarianNote && (
        <p className="mt-4 text-xs text-bl-black/55">{formula.vegetarianNote}</p>
      )}

      {footer && <div className="mt-auto pt-6">{footer}</div>}
    </div>
  );
};
